import {
  LayoutDashboard,
  Settings,
  Users,
  FileText,
  LogOut,
  Menu,
  X,
  Building2,
  CreditCard,
  TrendingUp,
  UserPlus,
  HelpCircle,
  Mail,
  Send,
  FileEdit,
  Headphones,
  Star,
  ListOrdered,
  Smartphone,
  BookOpen,
  MessageSquare
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import React from 'react'
import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { env } from '../config/environment'
import { useAuth } from '../contexts/AuthContext'

interface LayoutProps {
  children: React.ReactNode
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, token, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  // API accepts ?token= once, sets HttpOnly cookie for /api/admin/queues (browser cannot send Bearer on new-tab navigation).
  const queueDashboardUrl =
    user?.role === 'super_admin' && token
      ? `${env.apiBaseUrl}/api/admin/queues?token=${encodeURIComponent(token)}`
      : `${env.apiBaseUrl}/api/admin/queues`

  const navigation: {
    name: string
    href: string
    icon: LucideIcon
    external?: boolean
  }[] = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Businesses', href: '/businesses', icon: Building2 },
    { name: 'Business Leads', href: '/leads', icon: UserPlus },
    { name: 'Subscription Plans', href: '/subscriptions', icon: CreditCard },
    { name: 'Company Billing', href: '/company-subscriptions', icon: TrendingUp },
    { name: 'Support Management', href: '/support-management', icon: HelpCircle },
    { name: 'Public chats', href: '/public-chats', icon: MessageSquare },
    { name: 'Marketing Campaigns', href: '/marketing/campaigns', icon: Send },
    { name: 'Marketing Contacts', href: '/marketing/contacts', icon: Mail },
    { name: 'Email Templates', href: '/marketing/templates', icon: FileEdit },
    { name: 'Global Agents', href: '/global-agents', icon: Headphones },
    { name: 'Product Reviews', href: '/reviews', icon: Star },
    { name: 'Payroll docs', href: '/docs/payroll', icon: BookOpen },
    { name: 'Configuration', href: '/configuration', icon: Settings },
    { name: 'Users', href: '/users', icon: Users },
    {
      name: 'Queue dashboard',
      href: queueDashboardUrl,
      icon: ListOrdered,
      external: true
    },
    { name: 'Audit Logs', href: '/audit-logs', icon: FileText },
    { name: 'Mobile client logs', href: '/mobile-client-logs', icon: Smartphone }
  ]

  const navLinkClass = (isActive: boolean, base: string) =>
    `${base} ${
      isActive ? 'bg-primary-100 text-primary-900' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
    }`

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex w-full max-w-xs flex-col bg-white">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-6 w-6 text-white" />
            </button>
          </div>
          <div className="h-0 flex-1 overflow-y-auto pt-5 pb-4">
            <div className="flex flex-shrink-0 items-center px-4">
              <h1 className="text-xl font-bold text-gray-900">ServiceFlow Admin</h1>
            </div>
            <nav className="mt-5 space-y-1 px-2">
              {navigation.map((item) => {
                const Icon = item.icon
                const isActive = !item.external && location.pathname === item.href
                if (item.external) {
                  return (
                    <a
                      key={item.name}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={navLinkClass(false, 'group flex items-center px-2 py-2 text-base font-medium rounded-md')}
                      title="Bull Board on the API (use super_admin JWT or BULL_BOARD_BASIC_* in production)"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <Icon className="mr-4 h-6 w-6 flex-shrink-0" />
                      {item.name}
                    </a>
                  )
                }
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={navLinkClass(isActive, 'group flex items-center px-2 py-2 text-base font-medium rounded-md')}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon className="mr-4 h-6 w-6 flex-shrink-0" />
                    {item.name}
                  </Link>
                )
              })}
            </nav>
          </div>
          <div className="flex flex-shrink-0 border-t border-gray-200 p-4">
            <div className="flex items-center">
              <div className="ml-3">
                <p className="text-base font-medium text-gray-700">{user?.firstName} {user?.lastName}</p>
                <p className="text-sm font-medium text-gray-500">{user?.email}</p>
              </div>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex min-h-0 flex-1 flex-col border-r border-gray-200 bg-white">
          <div className="flex flex-1 flex-col overflow-y-auto pt-5 pb-4">
            <div className="flex flex-shrink-0 items-center px-4">
              <h1 className="text-xl font-bold text-gray-900">ServiceFlow Admin</h1>
            </div>
            <nav className="mt-5 flex-1 space-y-1 px-2">
              {navigation.map((item) => {
                const Icon = item.icon
                const isActive = !item.external && location.pathname === item.href
                if (item.external) {
                  return (
                    <a
                      key={item.name}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={navLinkClass(false, 'group flex items-center px-2 py-2 text-sm font-medium rounded-md')}
                      title="Bull Board (opens with your admin session)"
                    >
                      <Icon className="mr-3 h-6 w-6 flex-shrink-0" />
                      {item.name}
                    </a>
                  )
                }
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={navLinkClass(isActive, 'group flex items-center px-2 py-2 text-sm font-medium rounded-md')}
                  >
                    <Icon className="mr-3 h-6 w-6 flex-shrink-0" />
                    {item.name}
                  </Link>
                )
              })}
            </nav>
          </div>
          <div className="flex flex-shrink-0 border-t border-gray-200 p-4">
            <div className="flex w-full items-center">
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-gray-700">{user?.firstName} {user?.lastName}</p>
                <p className="text-xs font-medium text-gray-500">{user?.email}</p>
                <p className="text-xs text-gray-400 capitalize">{user?.role}</p>
              </div>
              <button
                onClick={handleLogout}
                className="ml-3 flex-shrink-0 rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-10 bg-white pl-1 pt-1 sm:pl-3 sm:pt-3 lg:hidden">
          <button
            type="button"
            className="-ml-0.5 -mt-0.5 inline-flex h-12 w-12 items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="py-6">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}

export default Layout
